import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import _ from "lodash";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import { InlineIcon } from "@iconify/react";
import {
  MyCafeteriaArchivedBenefitsMobileTableDef,
  myCafateriaArchivedBenefitsTableDef,
} from "my-cafeteria/configs";
import {
  useMyCafeteriaCurrentStatement,
  useMyCafeteriaMenu,
  useMyCafeteriaPeriods,
} from "my-cafeteria/hooks";
import { useArchivedStatementItems } from "my-cafeteria/hooks/use-archived-statement-items";
import {
  myCafateriaNewStatementItemState,
  myCafateriaShowNewStatementItemFormState,
} from "my-cafeteria/states";
import { OcCard, OcDataTable } from "oc/components";
import parseJdDate from "oc/utils/parse-jd-date";

type MyCafeteriaArchiveApplicationsCardProps = {
  contractCode: string;
  isMobile: boolean;
};

export const MyCafeteriaArchiveApplicationsCard = ({
  contractCode,
  isMobile,
}: MyCafeteriaArchiveApplicationsCardProps) => {
  const { t } = useTranslation();

  const [newItem, setNewItem] = useRecoilState(
    myCafateriaNewStatementItemState,
  );
  /** Open and close edit forms state */
  const [showNewItemForm, setShowNewItemForm] = useRecoilState(
    myCafateriaShowNewStatementItemFormState,
  );

  const { data: menuData } = useMyCafeteriaMenu({ contractCode });

  const { data: cafeteriaPeriods } = useMyCafeteriaPeriods();

  const { data, error, loading } = useArchivedStatementItems({
    contractCode,
  });

  const { data: currentStatement } = useMyCafeteriaCurrentStatement({
    contractCode,
  });

  const disableCopyButton = currentStatement?.state === "closed";

  const archivedPeriods = cafeteriaPeriods?.filter(
    (period: { periodStartDate: string }) =>
      period.periodStartDate !== currentStatement?.startDate,
  );

  const currentItems = data?.filter((item) => {
    if (
      item?.originalStartDate >= currentStatement?.startDate &&
      item.originalEndDate <= currentStatement?.endDate
    )
      return true;
    return false;
  });

  const archivedItems = data?.filter((item) => {
    if (
      item?.originalStartDate >= currentStatement?.startDate &&
      item.originalEndDate <= currentStatement?.endDate
    )
      return false;
    return true;
  });

  const extendedArchivedItems = archivedItems?.map((item: any) => {
    let newItem = _.cloneDeep(item);
    const isExist = currentItems.find(
      (currentItem) => currentItem?.menuItem?.code === item?.menuItem?.code,
    );

    // Check if there's already a duplicate with same code and same months
    const hasDuplicateMonths = currentItems?.some((currentItem: any) => {
      if (currentItem?.menuItem?.code !== item?.menuItem?.code) return false;
      return currentItem?.months === item?.months;
    });

    newItem.canCopy =
      (isExist && isExist.menuItem && isExist.menuItem.isReusable === false) ||
      hasDuplicateMonths
        ? false
        : true;
    return newItem;
  });

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const handleSelectRow = ({ item }: { item: any }) => {
    let copyItem = { ...newItem };
    let selectedMenuItem = menuData.find(
      (menuItem: any) => menuItem?.code === item.menuItem?.code,
    );
    let isExistMonth = selectedMenuItem?.availableMonths.includes(
      item.startDate,
    );
    // Calculate monthly amount by dividing the total by number of months
    const monthlyAmount = item.numberOfMonths > 0
      ? item.amount / item.numberOfMonths
      : item.amount;
    copyItem.amount = monthlyAmount;
    copyItem.benefitType = item?.menuItem?.code;
    copyItem.startDate = isExistMonth ? item.startDate : "";
    copyItem.endDate = isExistMonth ? item.endDate : "";
    copyItem.menuItemCode = item?.menuItem?.code;
    copyItem.type = item?.type;
    copyItem.isPeriodical = selectedMenuItem?.isPeriodical;
    copyItem.isPlanable = selectedMenuItem?.planable;
    copyItem.plan = item.plan;
    copyItem.isReusable = selectedMenuItem?.isReusable;
    setNewItem(copyItem);
    setShowNewItemForm(true);
    window.scrollTo({
      top: 436,
      left: 0,
      behavior: "smooth",
    });
  };

  const onSelectRow = (item: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === item?._id ? undefined : item?._id);
    } else {
      handleSelectRow(item);
    }
  };

  const [selectedPeriodIndex, setSelectedPeriodIndex] = React.useState(0);
  const selectedPeriod = archivedPeriods?.[selectedPeriodIndex];

  if (extendedArchivedItems?.length === 0) return null;

  const handleCanCopy = (item: any) => {
    // Use the pre-calculated canCopy property from extendedArchivedItems
    // Additional runtime checks
    if (disableCopyButton) return false;
    if (showNewItemForm) return false;

    return item.canCopy;
  };
  const periodStartDate = moment(
    parseJdDate(selectedPeriod?.periodStartDate),
  ).format("YYYY.MM.DD.");
  const periodEndDate = moment(selectedPeriod?.periodEndDate).format(
    "YYYY.MM.DD.",
  );

  const isFirstElement = selectedPeriodIndex === archivedPeriods?.length - 1;
  const isLastElement = selectedPeriodIndex === 0;

  const handleNextPeriod = () => {
    if (isLastElement) return;
    setSelectedPeriodIndex(selectedPeriodIndex - 1);
  };

  const handlePreviousPeriod = () => {
    if (isFirstElement) return;
    setSelectedPeriodIndex(selectedPeriodIndex + 1);
  };

  const filteredByPeriod = extendedArchivedItems?.filter((item) => {
    if (
      item?.originalStartDate >= selectedPeriod?.periodStartDate &&
      item?.originalEndDate <= selectedPeriod?.periodEndDate
    )
      return true;
    return false;
  });

  const list = {
    rows: filteredByPeriod,
  };

  const tableDef = isMobile
    ? MyCafeteriaArchivedBenefitsMobileTableDef
    : myCafateriaArchivedBenefitsTableDef;

  const filteredTableDef = {
    id: myCafateriaArchivedBenefitsTableDef?.id,
    columns: myCafateriaArchivedBenefitsTableDef?.columns?.filter(
      (column) =>
        !MyCafeteriaArchivedBenefitsMobileTableDef?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  return (
    <Box>
      <OcCard
        label={t("MY_CAFETERIA_PREVIOUS_BENEFITS")}
        error={error ? true : false}
      >
        <Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            paddingBottom="32px"
            paddingTop="8px"
          >
            <InlineIcon
              id="my-cafeteria-archive-app-left"
              icon="mdi:chevron-left"
              width="24px"
              color={isFirstElement ? "#C6CACC" : "#4d4d4d"}
              style={
                isFirstElement ? { cursor: "auto" } : { cursor: "pointer" }
              }
              onClick={handlePreviousPeriod}
            />
            <Box width="12px" />
            <Typography
              variant="h6"
              style={{ color: "#4D4D4D", textAlign: "center" }}
            >
              {periodStartDate} - {periodEndDate}
            </Typography>
            <Box width="12px" />
            <InlineIcon
              id="my-cafeteria-archive-app-right"
              icon="mdi:chevron-right"
              width="24px"
              color={isLastElement ? "#C6CACC" : "#4d4d4d"}
              style={isLastElement ? { cursor: "auto" } : { cursor: "pointer" }}
              onClick={handleNextPeriod}
            />
          </Box>
          <OcDataTable
            tableDef={tableDef}
            data={list}
            loading={loading}
            onSelectRow={onSelectRow}
            canCopy={handleCanCopy}
            filteredTableDef={filteredTableDef}
            handleMoreButton={handleSelectRow}
            selectedRowId={selectedRowId}
          />
        </Box>
      </OcCard>
    </Box>
  );
};

export default MyCafeteriaArchiveApplicationsCard;
